import React, { useState, useEffect, useRef, useCallback } from 'react';

const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAvqhg9a9UGp1QiJ6kF5j5RqMCaFzHj7CY';

// Team members for quick assignment
const TEAM_MEMBERS = [
  { id: 'jorge', name: 'Jorge', color: '#e85d04', avatar: 'J' },
  { id: 'kyle', name: 'Kyle', color: '#3b82f6', avatar: 'K' },
  { id: 'carlos', name: 'Carlos', color: '#16a34a', avatar: 'C' },
];

// Tag definitions → flyer template mapping
const DRIVE_TAGS = [
  { value: 'missing_shingles', label: 'Missing Shingles', icon: '🏚️', color: '#dc2626', flyer: 'Letter for app shingle blow off.docx' },
  { value: 'old_roof', label: 'Old Roof', icon: '📅', color: '#ea580c', flyer: 'Letter for app Old Roof.docx' },
  { value: 'storm_damage', label: 'Storm Damage', icon: '⛈️', color: '#2563eb', flyer: 'Letter for app Storm.docx' },
  { value: 'tornado_damage', label: 'Tornado', icon: '🌪️', color: '#7c3aed', flyer: 'Letter for app Tornado.docx' },
  { value: 'metal_roof_candidate', label: 'Metal Roof', icon: '🔩', color: '#6366f1', flyer: 'Letter for app Metal.docx' },
  { value: 'gutter_issues', label: 'Gutter', icon: '🌧️', color: '#0891b2', flyer: 'Letter for app Gutters.docx' },
  { value: 'homeowner_absent', label: 'Not Home', icon: '🚪', color: '#9ca3af', flyer: 'Letter for app generic.docx' },
  { value: 'leak_signs', label: 'Leak Signs', icon: '💧', color: '#0ea5e9', flyer: 'Letter for app all repairs.docx' },
  { value: 'commercial', label: 'Commercial', icon: '🏢', color: '#8b5cf6', flyer: 'Letter for app Commercial.docx' },
  { value: 'discontinued_shingles', label: 'Discontinued', icon: '⚠️', color: '#f59e0b', flyer: 'Letter for app Discontinued Shingle.docx' },
  { value: 'nice_house', label: 'Nice House', icon: '🏡', color: '#22c55e', flyer: null },
  { value: 'talked_to_owner', label: 'Talked', icon: '🗣️', color: '#10b981', flyer: null },
];

function DriveMode({ isVisible, onClose, user, onSaveToCRM, leads: appLeads, dbLeads }) {
  // GPS state
  const [gpsPos, setGpsPos] = useState(null);
  const [gpsHeading, setGpsHeading] = useState(0);
  const [gpsSpeed, setGpsSpeed] = useState(0);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);

  // UI state
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [houseData, setHouseData] = useState(null);
  const [loadingHouse, setLoadingHouse] = useState(false);
  const [activeTags, setActiveTags] = useState([]);
  const [customTag, setCustomTag] = useState('');
  const [showCustomTag, setShowCustomTag] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState(null);
  const [followGps, setFollowGps] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showLeadsDrawer, setShowLeadsDrawer] = useState(false);
  const [leadsSearch, setLeadsSearch] = useState('');
  const [isSatellite, setIsSatellite] = useState(true);
  const [housesOn, setHousesOn] = useState(true);
  const [currentStreet, setCurrentStreet] = useState('');
  const [currentNeighborhood, setCurrentNeighborhood] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [showStreetView, setShowStreetView] = useState(false);
  const [previewTab, setPreviewTab] = useState('roof'); // 'roof' | 'street'

  // Demo/Virtual Drive
  const [demoActive, setDemoActive] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [demoCoach, setDemoCoach] = useState(null); // coaching tooltip text
  const demoTimerRef = useRef(null);

  // Drive log
  const [driveLog, setDriveLog] = useState(() => {
    try {
      const saved = localStorage.getItem('roofleads_drive_log');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Refs
  const mapContainerRef = useRef(null);
  const svContainerRef = useRef(null);
  const mapRef = useRef(null);
  const watchIdRef = useRef(null);
  const gpsMarkerRef = useRef(null);
  const gpsArrowRef = useRef(null);
  const accCircleRef = useRef(null);
  const tapMarkerRef = useRef(null);
  const houseOverlaysRef = useRef([]);
  const loggedMarkersRef = useRef([]);
  const loadHousesTimerRef = useRef(null);
  const photoInputRef = useRef(null);
  const reverseGeocodeTimerRef = useRef(null);
  const lastOverpassBoundsRef = useRef(null);

  // Persist drive log
  useEffect(() => {
    localStorage.setItem('roofleads_drive_log', JSON.stringify(driveLog));
  }, [driveLog]);

  // Lock screen orientation to portrait on mobile + keep screen awake
  useEffect(() => {
    if (!isVisible) return;
    // Try to lock orientation
    try {
      screen.orientation?.lock?.('portrait').catch(() => {});
    } catch {}
    // Keep screen awake
    let wakeLock = null;
    try {
      navigator.wakeLock?.request?.('screen').then(wl => { wakeLock = wl; }).catch(() => {});
    } catch {}
    return () => {
      try { screen.orientation?.unlock?.(); } catch {}
      try { wakeLock?.release?.(); } catch {}
    };
  }, [isVisible]);

  // Auto-show demo on first ever Drive Mode launch
  useEffect(() => {
    if (!isVisible || !mapReady) return;
    const seen = localStorage.getItem('roofleads_demo_seen');
    if (!seen) {
      localStorage.setItem('roofleads_demo_seen', '1');
      // Small delay to let map load
      setTimeout(() => startDemo(), 2000);
    }
  }, [isVisible, mapReady]); // eslint-disable-line

  // ========== MAP INIT ==========
  useEffect(() => {
    if (!isVisible || !window.google?.maps) return;
    if (mapRef.current) return; // already initialized

    const initMap = () => {
      if (!mapContainerRef.current) return;

      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: 39.7684, lng: -86.1581 },
        zoom: 19,
        mapTypeId: 'hybrid',
        tilt: 60,
        heading: 0,
        disableDefaultUI: true,
        zoomControl: false,
        gestureHandling: 'greedy',
        clickableIcons: false,
        mapId: '90f87356969d889c', // Vector map — required for tilt/heading/rotation
      });

      mapRef.current = map;

      map.addListener('click', (e) => {
        onMapTap(e.latLng.lat(), e.latLng.lng());
      });

      map.addListener('dragstart', () => {
        setFollowGps(false);
      });

      map.addListener('idle', () => {
        if (housesOn) loadHousesInView();
      });

      setMapReady(true);
    };

    setTimeout(initMap, 150);

    return () => {
      houseOverlaysRef.current.forEach(o => o.setMap(null));
      loggedMarkersRef.current.forEach(m => m.setMap(null));
      if (gpsMarkerRef.current) gpsMarkerRef.current.setMap(null);
      if (gpsArrowRef.current) gpsArrowRef.current.setMap(null);
      if (accCircleRef.current) accCircleRef.current.setMap(null);
      if (tapMarkerRef.current) tapMarkerRef.current.setMap(null);
      mapRef.current = null;
      setMapReady(false);
    };
  }, [isVisible]); // eslint-disable-line

  // ========== GPS ==========
  useEffect(() => {
    if (!isVisible || !navigator.geolocation) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGpsPos(p);
        setGpsAccuracy(Math.round(pos.coords.accuracy));
        setGpsSpeed(pos.coords.speed || 0);
        if (pos.coords.heading != null) setGpsHeading(pos.coords.heading);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [isVisible]);

  // ========== GPS MARKER + FOLLOW (Navigation style) ==========
  useEffect(() => {
    if (!mapReady || !gpsPos || !mapRef.current) return;
    const map = mapRef.current;
    const google = window.google;

    if (!gpsMarkerRef.current) {
      // Navigation arrow — always points UP because map rotates with heading
      gpsArrowRef.current = new google.maps.Marker({
        position: gpsPos, map, zIndex: 10000, clickable: false,
        icon: {
          path: 'M 0,-18 L 11,12 L 0,6 L -11,12 Z',
          scale: 2.5, fillColor: '#4285F4', fillOpacity: 1,
          strokeColor: '#fff', strokeWeight: 3,
          rotation: 0,
          anchor: new google.maps.Point(0, 0),
        },
      });
      // Glow ring
      gpsMarkerRef.current = new google.maps.Marker({
        position: gpsPos, map, zIndex: 9999, clickable: false,
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 20,
          fillColor: '#4285F4', fillOpacity: 0.15,
          strokeColor: '#4285F4', strokeOpacity: 0.3, strokeWeight: 2,
        },
      });
      // Accuracy circle
      accCircleRef.current = new google.maps.Circle({
        center: gpsPos, radius: gpsAccuracy || 10, map,
        fillColor: '#4285F4', fillOpacity: 0.06,
        strokeColor: '#4285F4', strokeOpacity: 0.15, strokeWeight: 1,
        clickable: false,
      });
    } else {
      gpsMarkerRef.current.setPosition(gpsPos);
      gpsArrowRef.current.setPosition(gpsPos);
      accCircleRef.current.setCenter(gpsPos);
      accCircleRef.current.setRadius(Math.max(gpsAccuracy || 10, 3));
    }

    if (followGps) {
      const heading = gpsHeading || 0;

      // Offset center forward so user appears in bottom 1/3
      const offsetDist = 0.0008; // ~80m ahead of user
      const headingRad = (heading * Math.PI) / 180;
      const aheadCenter = {
        lat: gpsPos.lat + Math.cos(headingRad) * offsetDist,
        lng: gpsPos.lng + Math.sin(headingRad) * offsetDist,
      };

      // moveCamera = atomic update of center + heading + tilt + zoom (no jank)
      try {
        map.moveCamera({
          center: aheadCenter,
          zoom: 19,
          heading: heading,
          tilt: 67, // Steep tilt like real Google Nav (max is ~67.5)
        });
      } catch(e) {
        // Fallback
        map.setCenter(aheadCenter);
        try { map.setHeading(heading); } catch(e2) {}
        try { map.setTilt(60); } catch(e2) {}
      }
    }

    // Reverse geocode for current street name (throttled)
    clearTimeout(reverseGeocodeTimerRef.current);
    reverseGeocodeTimerRef.current = setTimeout(() => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: gpsPos }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const comps = results[0].address_components;
          const route = comps.find(c => c.types.includes('route'))?.long_name || '';
          const neighborhood = comps.find(c => c.types.includes('neighborhood'))?.long_name
            || comps.find(c => c.types.includes('sublocality'))?.long_name
            || comps.find(c => c.types.includes('locality'))?.long_name || '';
          setCurrentStreet(route);
          setCurrentNeighborhood(neighborhood);
        }
      });
    }, 3000);
  }, [gpsPos, gpsHeading, gpsAccuracy, mapReady, followGps]);

  // ========== LOAD HOUSES VIA OVERPASS ==========
  const loadHousesInView = useCallback(() => {
    clearTimeout(loadHousesTimerRef.current);
    loadHousesTimerRef.current = setTimeout(() => {
      if (!mapRef.current || !window.google) return;
      const map = mapRef.current;
      const b = map.getBounds();
      if (!b) return;
      const z = map.getZoom();
      if (z < 18) {
        // Only show labels at zoom 18+ to prevent flood
        houseOverlaysRef.current.forEach(o => o.setMap(null));
        houseOverlaysRef.current = [];
        return;
      }

      const sw = b.getSouthWest();
      const ne = b.getNorthEast();

      // Skip if bounds haven't changed much
      const last = lastOverpassBoundsRef.current;
      if (last) {
        const dlat = Math.abs(sw.lat() - last.swLat) + Math.abs(ne.lat() - last.neLat);
        const dlng = Math.abs(sw.lng() - last.swLng) + Math.abs(ne.lng() - last.neLng);
        if (dlat + dlng < 0.001) return; // barely moved
      }
      lastOverpassBoundsRef.current = { swLat: sw.lat(), swLng: sw.lng(), neLat: ne.lat(), neLng: ne.lng() };

      const bbox = `${sw.lat()},${sw.lng()},${ne.lat()},${ne.lng()}`;
      const query = `[out:json][timeout:8];(node["addr:housenumber"](${bbox});way["addr:housenumber"](${bbox}););out center 200;`;

      fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'data=' + encodeURIComponent(query)
      })
        .then(r => r.json())
        .then(data => {
          // Clear old overlays
          houseOverlaysRef.current.forEach(o => o.setMap(null));
          houseOverlaysRef.current = [];

          const els = data.elements || [];
          const loggedAddresses = new Set(
            driveLog.map(e => `${e.lat?.toFixed(4)},${e.lng?.toFixed(4)}`)
          );

          // De-duplicate and space out labels — max 35 visible
          const MAX_LABELS = 35;
          const MIN_SPACING = 0.00015; // ~15m apart to prevent overlap
          const placed = [];

          const filtered = els.filter(el => {
            const lat = el.lat || el.center?.lat;
            const lng = el.lon || el.center?.lon;
            if (!lat || !lng) return false;
            if (!el.tags['addr:housenumber']) return false;
            // Check spacing against already placed labels
            const tooClose = placed.some(p =>
              Math.abs(p.lat - lat) < MIN_SPACING && Math.abs(p.lng - lng) < MIN_SPACING
            );
            if (tooClose) return false;
            placed.push({ lat, lng });
            return placed.length <= MAX_LABELS;
          });

          filtered.forEach(el => {
            const lat = el.lat || el.center?.lat;
            const lng = el.lon || el.center?.lon;

            const num = el.tags['addr:housenumber'] || '';
            const street = el.tags['addr:street'] || '';
            // At high zoom show full address, otherwise just house number
            const label = z >= 19 ? (num + (street ? ' ' + street : '')) : num;
            if (!label.trim()) return;

            const isLogged = loggedAddresses.has(`${lat.toFixed(4)},${lng.toFixed(4)}`);

            // Create custom overlay for house label
            const overlay = new window.google.maps.OverlayView();
            overlay._lat = lat;
            overlay._lng = lng;
            overlay._label = label;
            overlay._isLogged = isLogged;

            overlay.onAdd = function () {
              const div = document.createElement('div');
              div.className = 'dm-house-tag' + (isLogged ? ' logged' : '');
              div.textContent = label;
              div.onclick = function (e) {
                e.stopPropagation();
                onMapTap(lat, lng);
              };
              this._div = div;
              this.getPanes().floatPane.appendChild(div);
            };

            overlay.draw = function () {
              const proj = this.getProjection();
              if (!proj) return;
              const pos = proj.fromLatLngToDivPixel(
                new window.google.maps.LatLng(this._lat, this._lng)
              );
              if (pos && this._div) {
                this._div.style.position = 'absolute';
                this._div.style.left = (pos.x - this._div.offsetWidth / 2) + 'px';
                this._div.style.top = (pos.y - 22) + 'px';
              }
            };

            overlay.onRemove = function () {
              if (this._div) this._div.remove();
            };

            overlay.setMap(map);
            houseOverlaysRef.current.push(overlay);
          });
        })
        .catch(() => {});
    }, 600);
  }, [driveLog]); // eslint-disable-line

  // Reload houses when toggled
  useEffect(() => {
    if (mapReady && housesOn) {
      loadHousesInView();
    } else if (mapReady && !housesOn) {
      houseOverlaysRef.current.forEach(o => o.setMap(null));
      houseOverlaysRef.current = [];
    }
  }, [housesOn, mapReady, loadHousesInView]);

  // ========== LOGGED MARKERS ==========
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    loggedMarkersRef.current.forEach(m => m.setMap(null));
    loggedMarkersRef.current = [];

    driveLog.forEach((entry) => {
      if (!entry.lat || !entry.lng) return;
      const primaryTag = entry.tags?.[0];
      const tagDef = DRIVE_TAGS.find(t => t.value === primaryTag);
      const color = tagDef?.color || '#22c55e';

      const marker = new window.google.maps.Marker({
        position: { lat: entry.lat, lng: entry.lng },
        map: mapRef.current,
        zIndex: 100,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10, fillColor: color, fillOpacity: 0.9,
          strokeColor: '#fff', strokeWeight: 2,
        },
        title: entry.streetAddress || entry.address,
      });

      marker.addListener('click', () => {
        onMapTap(entry.lat, entry.lng);
      });

      loggedMarkersRef.current.push(marker);
    });
  }, [driveLog, mapReady]); // eslint-disable-line

  // ========== MAP TAP → REVERSE GEOCODE + PROPERTY LOOKUP ==========
  const onMapTap = useCallback(async (lat, lng) => {
    if (!window.google?.maps) return;

    setShowSummary(false);
    setShowStreetView(false);
    setSelectedHouse({ lat, lng });
    setHouseData(null);
    setLoadingHouse(true);
    setActiveTags([]);
    setPhotos([]);
    setNotes('');
    setShowCustomTag(false);
    setCustomTag('');
    setAssignedTo(null);
    setPreviewTab('roof');

    // Drop a pin
    if (tapMarkerRef.current) tapMarkerRef.current.setMap(null);
    if (mapRef.current) {
      tapMarkerRef.current = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapRef.current,
        zIndex: 9998,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 14, fillColor: '#ef4444', fillOpacity: 0.9,
          strokeColor: '#fff', strokeWeight: 3,
        },
        animation: window.google.maps.Animation.DROP,
      });
    }

    try {
      // Reverse geocode
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) resolve(results[0]);
          else reject(new Error('Geocode failed'));
        });
      });

      const comps = result.address_components;
      const streetNumber = comps.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = comps.find(c => c.types.includes('route'))?.long_name || '';
      const city = comps.find(c => c.types.includes('locality'))?.long_name || '';
      const state = comps.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
      const zip = comps.find(c => c.types.includes('postal_code'))?.short_name || '';
      const streetAddress = streetNumber && route ? `${streetNumber} ${route}` : '';

      setSelectedHouse({
        lat, lng,
        address: result.formatted_address,
        streetAddress: streetAddress.toUpperCase(),
        city, state, zip,
      });

      // Property lookup
      if (streetAddress) {
        const fullAddr = `${streetAddress}, ${city}, ${state} ${zip}`;
        try {
          const resp = await fetch(
            `${PROPERTY_API_URL}/lookup?address=${encodeURIComponent(fullAddr)}&userId=${encodeURIComponent(localStorage.getItem('leadgen_client_id') || 'anon')}`,
            { headers: { 'X-API-Key': PROPERTY_API_KEY } }
          );
          const data = await resp.json();
          if (data.success) {
            setHouseData({
              ownerName: data.owner?.names?.filter(n => n?.trim()).join(', ') || '',
              ownerOccupied: data.ownerOccupied ?? data.owner?.ownerOccupied,
              assessedValue: data.valuation?.assessedValue,
              sqft: data.property?.squareFootage,
              yearBuilt: data.property?.yearBuilt,
              bedrooms: data.property?.bedrooms,
              bathrooms: data.property?.bathrooms,
              roofSqft: data.roofEstimate?.roofSquareFootage,
              roofAge: data.roofEstimate?.roofAge,
              roofAgeConfidence: data.roofEstimate?.roofAgeConfidence,
              roofType: data.features?.roofType,
              needsReplacement: data.roofEstimate?.needsReplacement,
              costEstimates: data.roofEstimate?.costEstimates,
              lastSaleDate: data.valuation?.lastSaleDate,
              lastSalePrice: data.valuation?.lastSalePrice,
              enriched: true,
            });
          } else {
            setHouseData({ error: data.error || 'Not found', enriched: false });
          }
        } catch {
          setHouseData({ error: 'Lookup failed', enriched: false });
        }
      } else {
        setHouseData({ error: 'No street address found', enriched: false });
      }
    } catch {
      setSelectedHouse(prev => ({ ...prev, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` }));
      setHouseData({ error: 'Could not resolve address', enriched: false });
    }

    setLoadingHouse(false);
  }, []);

  // Check for drive destination from Daily Plan (must be after onMapTap)
  useEffect(() => {
    if (!isVisible || !mapReady) return;
    try {
      const dest = localStorage.getItem('roofleads_drive_dest');
      if (dest) {
        localStorage.removeItem('roofleads_drive_dest');
        const lead = JSON.parse(dest);
        if (lead.lat && lead.lng) {
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.panTo({ lat: lead.lat, lng: lead.lng });
              mapRef.current.setZoom(19);
              setFollowGps(false);
            }
            onMapTap(lead.lat, lead.lng);
          }, 1000);
        } else if (lead.address && window.google?.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: lead.address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const loc = results[0].geometry.location;
              const lat = loc.lat(), lng = loc.lng();
              if (mapRef.current) {
                mapRef.current.panTo({ lat, lng });
                mapRef.current.setZoom(19);
                setFollowGps(false);
              }
              setTimeout(() => onMapTap(lat, lng), 500);
            }
          });
        }
      }
    } catch {}
  }, [isVisible, mapReady, onMapTap]);

  // ========== ACTIONS ==========
  const toggleTag = (v) => {
    setActiveTags(prev => prev.includes(v) ? prev.filter(t => t !== v) : [...prev, v]);
  };

  const addCustomTag = () => {
    if (customTag.trim()) {
      const v = customTag.trim().toLowerCase().replace(/\s+/g, '_');
      if (!activeTags.includes(v)) setActiveTags(prev => [...prev, v]);
      setCustomTag('');
      setShowCustomTag(false);
    }
  };

  const handlePhotoCapture = (e) => {
    Array.from(e.target.files || []).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos(prev => [...prev, { dataUrl: ev.target.result, timestamp: new Date().toISOString() }]);
      };
      reader.readAsDataURL(file);
    });
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const saveHouse = async () => {
    if (!selectedHouse) return;
    setSaving(true);

    const entry = {
      id: `drive_${Date.now()}`,
      lat: selectedHouse.lat, lng: selectedHouse.lng,
      address: selectedHouse.address,
      streetAddress: selectedHouse.streetAddress,
      city: selectedHouse.city, state: selectedHouse.state, zip: selectedHouse.zip,
      ownerName: houseData?.ownerName || '',
      ownerOccupied: houseData?.ownerOccupied,
      assessedValue: houseData?.assessedValue,
      sqft: houseData?.sqft,
      yearBuilt: houseData?.yearBuilt,
      roofSqft: houseData?.roofSqft,
      roofAge: houseData?.roofAge,
      roofType: houseData?.roofType,
      tags: activeTags,
      photos: photos.map(p => ({ dataUrl: p.dataUrl, timestamp: p.timestamp })),
      notes, assignedTo,
      timestamp: new Date().toISOString(),
      enriched: houseData?.enriched || false,
    };

    setDriveLog(prev => [entry, ...prev]);

    // Save to CRM
    if (onSaveToCRM && selectedHouse.streetAddress) {
      try {
        await onSaveToCRM([{
          streetAddress: selectedHouse.streetAddress,
          city: selectedHouse.city, state: selectedHouse.state, zip: selectedHouse.zip,
          ownerName: houseData?.ownerName || '',
          ownerOccupied: houseData?.ownerOccupied,
          assessedValue: houseData?.assessedValue,
          sqft: houseData?.sqft, yearBuilt: houseData?.yearBuilt,
          roofSqft: houseData?.roofSqft, roofAge: houseData?.roofAge,
          roofType: houseData?.roofType,
          tag: activeTags[0] || null,
          enriched: houseData?.enriched || false,
          lat: selectedHouse.lat, lng: selectedHouse.lng,
          source: 'drive_mode',
        }]);
      } catch { }
    }

    setSaving(false);
    dismissHouse();
  };

  const dismissHouse = () => {
    setSelectedHouse(null);
    setHouseData(null);
    setActiveTags([]);
    setPhotos([]);
    setNotes('');
    setShowStreetView(false);
    if (tapMarkerRef.current) { tapMarkerRef.current.setMap(null); tapMarkerRef.current = null; }
  };

  const openStreetView = () => {
    if (!selectedHouse) return;
    setShowStreetView(true);

    // Wait for the overlay to render, then init panorama
    setTimeout(() => {
      if (!svContainerRef.current) return;
      const pano = new window.google.maps.StreetViewPanorama(svContainerRef.current, {
        position: { lat: selectedHouse.lat, lng: selectedHouse.lng },
        pov: { heading: 0, pitch: 20 },
        zoom: 0,
        disableDefaultUI: false,
        showRoadLabels: true,
      });

      const svService = new window.google.maps.StreetViewService();
      svService.getPanorama(
        { location: { lat: selectedHouse.lat, lng: selectedHouse.lng }, radius: 100 },
        (data, status) => {
          if (status === 'OK') {
            pano.setPano(data.location.pano);
            const svLat = data.location.latLng.lat();
            const svLng = data.location.latLng.lng();
            const heading = window.google.maps.geometry.spherical.computeHeading(
              new window.google.maps.LatLng(svLat, svLng),
              new window.google.maps.LatLng(selectedHouse.lat, selectedHouse.lng)
            );
            pano.setPov({ heading, pitch: 20 });
          }
        }
      );
    }, 300);
  };

  // ========== DEMO / VIRTUAL DRIVE ==========
  const DEMO_ROUTE = [
    // Silver Creek Dr, Indianapolis — real neighborhood
    { lat: 39.67280, lng: -86.04200, heading: 45, action: 'drive', coach: '🚗 Welcome to Drive Mode! You\'re virtually driving through Silver Creek Dr neighborhood.' },
    { lat: 39.67320, lng: -86.04140, heading: 40, action: 'drive', coach: '👀 See the house addresses on the map? Each label is tappable.' },
    { lat: 39.67360, lng: -86.04080, heading: 35, action: 'drive', coach: '🏠 Notice the houses on both sides. Look for roof damage signs.' },
    { lat: 39.67140, lng: -86.04350, heading: 50, action: 'tap', coach: '👆 Tapping a house shows the owner name, home value, roof age, and more!' },
    { lat: 39.67140, lng: -86.04350, heading: 50, action: 'show_tags', coach: '🏷️ Tag what you see: Missing Shingles, Old Roof, Not Home... Tags match to your flyer templates!' },
    { lat: 39.67140, lng: -86.04350, heading: 50, action: 'show_photo', coach: '📸 Snap a photo of the roof! It attaches to this house in the CRM.' },
    { lat: 39.67140, lng: -86.04350, heading: 50, action: 'show_crew', coach: '👥 Assign to Jorge, Kyle, or Carlos so you know who logged it.' },
    { lat: 39.67140, lng: -86.04350, heading: 50, action: 'show_save', coach: '💾 Hit Save to CRM — done! Move to the next house. At the office, filter by tags to match flyers.' },
    { lat: 39.67180, lng: -86.04300, heading: 30, action: 'drive', coach: '🛰️ The Rooftop + Street View previews help you see damage before knocking.' },
    { lat: 39.67220, lng: -86.04250, heading: 25, action: 'drive', coach: '📊 Tap the summary button (📊) to see today\'s stats — houses logged, tags, flyer matching.' },
    { lat: 39.67260, lng: -86.04200, heading: 20, action: 'done', coach: '✅ That\'s it! You\'re ready to drive for real. Exit demo and hit 🚗 Drive to start.' },
  ];

  const startDemo = useCallback(() => {
    setDemoActive(true);
    setDemoStep(0);
    setFollowGps(false); // Don't follow real GPS during demo
    dismissHouse();

    const map = mapRef.current;
    if (!map) return;

    // Pan to demo start
    const start = DEMO_ROUTE[0];
    try {
      map.moveCamera({ center: { lat: start.lat, lng: start.lng }, zoom: 19, heading: start.heading, tilt: 67 });
    } catch(e) {
      map.setCenter({ lat: start.lat, lng: start.lng });
    }
    setDemoCoach(start.coach);
  }, []); // eslint-disable-line

  const advanceDemo = useCallback(() => {
    const nextStep = demoStep + 1;
    if (nextStep >= DEMO_ROUTE.length) {
      // Demo done
      setDemoActive(false);
      setDemoStep(0);
      setDemoCoach(null);
      dismissHouse();
      setFollowGps(true);
      return;
    }

    setDemoStep(nextStep);
    const step = DEMO_ROUTE[nextStep];
    setDemoCoach(step.coach);

    const map = mapRef.current;
    if (!map) return;

    if (step.action === 'drive' || step.action === 'done') {
      // Animate camera to next position
      try {
        map.moveCamera({
          center: { lat: step.lat, lng: step.lng },
          zoom: 19,
          heading: step.heading,
          tilt: 67,
        });
      } catch(e) {
        map.panTo({ lat: step.lat, lng: step.lng });
      }
    }

    if (step.action === 'tap') {
      // Auto-tap a house
      onMapTap(step.lat, step.lng);
    }
  }, [demoStep, onMapTap]); // eslint-disable-line

  const stopDemo = useCallback(() => {
    setDemoActive(false);
    setDemoStep(0);
    setDemoCoach(null);
    clearTimeout(demoTimerRef.current);
    setFollowGps(true);
  }, []);

  // Session stats
  const getSessionStats = () => {
    const today = new Date().toDateString();
    const todayLogs = driveLog.filter(e => new Date(e.timestamp).toDateString() === today);
    const tagCounts = {};
    todayLogs.forEach(e => (e.tags || []).forEach(t => { tagCounts[t] = (tagCounts[t] || 0) + 1; }));
    const byCrew = {};
    todayLogs.forEach(e => { if (e.assignedTo) byCrew[e.assignedTo] = (byCrew[e.assignedTo] || 0) + 1; });
    return {
      total: todayLogs.length,
      tagCounts,
      byCrew,
      withPhotos: todayLogs.filter(e => e.photos?.length > 0).length,
      zips: [...new Set(todayLogs.map(e => e.zip).filter(Boolean))],
    };
  };

  // Navigate to a lead from the list
  const goToLead = useCallback((lead) => {
    setShowLeadsDrawer(false);
    setLeadsSearch('');

    const lat = lead.lat || null;
    const lng = lead.lng || null;
    const streetAddr = lead.street_address || lead.streetAddress || '';
    const city = lead.city || '';
    const state = lead.state || '';
    const zip = lead.zip_code || lead.zip || '';

    const navigateToCoords = (lt, ln) => {
      if (mapRef.current) {
        mapRef.current.panTo({ lat: lt, lng: ln });
        mapRef.current.setZoom(19);
        setFollowGps(false);
      }
      // Auto-tap to open the bottom sheet
      setTimeout(() => onMapTap(lt, ln), 500);
    };

    if (lat && lng) {
      navigateToCoords(lat, lng);
    } else if (streetAddr && window.google?.maps) {
      // Geocode
      const addr = `${streetAddr}, ${city}, ${state} ${zip}`;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: addr }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = results[0].geometry.location;
          navigateToCoords(loc.lat(), loc.lng());
        }
      });
    }
  }, [onMapTap]);

  // Build merged leads list from app leads + DB leads
  const getAllLeads = () => {
    const combined = [];
    const seen = new Set();

    // App session leads first
    (appLeads || []).forEach(l => {
      const key = (l.streetAddress || '').toUpperCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        combined.push({
          streetAddress: l.streetAddress, city: l.city, state: l.state, zip: l.zip,
          ownerName: l.ownerName, assessedValue: l.assessedValue,
          lat: l.lat, lng: l.lng, enriched: l.enriched,
          leadQuality: l.leadQuality, source: 'session',
        });
      }
    });

    // DB/CRM leads
    (dbLeads || []).forEach(l => {
      const key = (l.street_address || l.streetAddress || '').toUpperCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        combined.push({
          streetAddress: l.street_address || l.streetAddress,
          city: l.city, state: l.state, zip: l.zip_code || l.zip,
          ownerName: l.owner_name || l.ownerName,
          assessedValue: l.assessed_value || l.assessedValue,
          lat: l.lat, lng: l.lng, enriched: l.enriched,
          leadQuality: l.priority || l.leadQuality, source: 'crm',
          tag: l.tag,
        });
      }
    });

    // Filter by search
    if (leadsSearch.trim()) {
      const q = leadsSearch.toLowerCase();
      return combined.filter(l =>
        (l.streetAddress || '').toLowerCase().includes(q) ||
        (l.ownerName || '').toLowerCase().includes(q) ||
        (l.zip || '').includes(q) ||
        (l.city || '').toLowerCase().includes(q)
      );
    }

    return combined;
  };

  if (!isVisible) return null;

  const speedMph = Math.round((gpsSpeed || 0) * 2.237);
  const stats = getSessionStats();
  const filteredLeads = getAllLeads();

  return (
    <div className="dm">
      {/* Hidden file input for camera */}
      <input ref={photoInputRef} type="file" accept="image/*" capture="environment" multiple
        style={{ display: 'none' }} onChange={handlePhotoCapture} />

      {/* ===== TOP NAV HUD (Google Maps style) ===== */}
      <div className="dm-hud">
        <div className="dm-hud-left">
          <button className="dm-hud-exit" onClick={onClose}>✕</button>
          <div className="dm-hud-street-info">
            <div className="dm-hud-street">{currentStreet || 'Drive Mode'}</div>
            {currentNeighborhood && (
              <div className="dm-hud-neighborhood">{currentNeighborhood}</div>
            )}
          </div>
        </div>
        <div className="dm-hud-right">
          {!demoActive && (
            <button className="dm-demo-btn" onClick={startDemo}>🎮 Demo</button>
          )}
          <div className={`dm-hud-gps ${(gpsAccuracy || 999) <= 5 ? 'excellent' : (gpsAccuracy || 999) <= 15 ? 'good' : 'fair'}`}>
            <span className="dm-gps-dot" />
            ±{gpsAccuracy || '—'}m
          </div>
        </div>
      </div>

      {/* ===== MAP ===== */}
      <div className="dm-map" ref={mapContainerRef} />

      {/* Compass indicator */}
      <div className="dm-compass" style={{ transform: `rotate(${-(gpsHeading || 0)}deg)` }}>
        <span className="dm-compass-n">N</span>
        <span className="dm-compass-arrow">▲</span>
      </div>

      {/* Speed pill — bottom-left like Google Maps */}
      <div className="dm-speed-pill">
        <span className="spd-val">{speedMph}</span>
        <span className="spd-unit">mph</span>
      </div>

      {/* ===== STREET VIEW OVERLAY ===== */}
      {showStreetView && (
        <div className="dm-sv-overlay">
          <div className="dm-sv-bar">
            <span className="dm-sv-title">{selectedHouse?.streetAddress || 'Street View'}</span>
            <button className="dm-sv-close" onClick={() => setShowStreetView(false)}>✕</button>
          </div>
          <div className="dm-sv-pano" ref={svContainerRef} />
        </div>
      )}

      {/* ===== RIGHT FABs ===== */}
      <div className={`dm-fabs ${selectedHouse ? 'sheet-open' : ''}`}>
        <button className={`dm-fab ${followGps ? 'active' : ''}`}
          onClick={() => {
            setFollowGps(true);
            if (gpsPos && mapRef.current) {
              try {
                mapRef.current.moveCamera({ center: gpsPos, zoom: 19, heading: gpsHeading || 0, tilt: 67 });
              } catch(e) {
                mapRef.current.panTo(gpsPos);
              }
            }
          }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="3" /><path d="M12 2v4m0 12v4M2 12h4m12 0h4" />
          </svg>
        </button>
        <button className={`dm-fab ${housesOn ? 'active' : ''}`}
          onClick={() => setHousesOn(p => !p)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </button>
        <button className={`dm-fab ${isSatellite ? 'active' : ''}`}
          onClick={() => { setIsSatellite(p => !p); if (mapRef.current) mapRef.current.setMapTypeId(isSatellite ? 'roadmap' : 'hybrid'); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" />
          </svg>
        </button>
        <button className="dm-fab" onClick={() => setShowSummary(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
          </svg>
        </button>
        <button className="dm-fab" onClick={() => setShowLeadsDrawer(true)} title="My Leads">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      </div>

      {/* ===== BOTTOM MINI BAR (when no house selected) ===== */}
      {!selectedHouse && !showSummary && (
        <div className="dm-bottom-bar">
          <div className="dm-bar-stat">{stats.total} houses logged today</div>
          <button className="dm-bar-camera" onClick={() => photoInputRef.current?.click()}>
            📸
          </button>
        </div>
      )}

      {/* ===== HOUSE BOTTOM SHEET ===== */}
      {selectedHouse && !showSummary && (
        <div className="dm-sheet">
          <div className="dm-sheet-handle" />

          {/* Address */}
          <div className="dm-sheet-head">
            <div className="dm-sheet-addr-wrap">
              <div className="dm-sheet-addr">{selectedHouse.streetAddress || 'Loading...'}</div>
              <div className="dm-sheet-city">{[selectedHouse.city, selectedHouse.state, selectedHouse.zip].filter(Boolean).join(', ')}</div>
            </div>
            <button className="dm-sheet-sv" onClick={openStreetView} title="Street View">
              🛣️
            </button>
            <button className="dm-sheet-x" onClick={dismissHouse}>✕</button>
          </div>

          {/* Rooftop + Street View side by side */}
          {selectedHouse.lat && selectedHouse.lng && (
            <div className="dm-preview-duo">
              <div className="dm-preview-card" onClick={() => {
                window.open(`https://www.google.com/maps/@${selectedHouse.lat},${selectedHouse.lng},20z/data=!3m1!1e3`, '_blank');
              }}>
                <img
                  src={`https://maps.googleapis.com/maps/api/staticmap?center=${selectedHouse.lat},${selectedHouse.lng}&zoom=20&size=600x400&maptype=satellite&markers=color:red%7Csize:small%7C${selectedHouse.lat},${selectedHouse.lng}&key=${GMAPS_KEY}`}
                  alt="Rooftop" className="dm-preview-card-img"
                />
                <div className="dm-preview-card-label">🛰️ Rooftop</div>
              </div>
              <div className="dm-preview-card" onClick={() => openStreetView()}>
                <img
                  src={`https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${selectedHouse.lat},${selectedHouse.lng}&source=outdoor&pitch=15&key=${GMAPS_KEY}`}
                  alt="Street View" className="dm-preview-card-img"
                  onError={e => {
                    if (!e.target.dataset.fallback) {
                      e.target.dataset.fallback = '1';
                      e.target.src = `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${selectedHouse.lat},${selectedHouse.lng}&pitch=15&key=${GMAPS_KEY}`;
                    }
                  }}
                />
                <div className="dm-preview-card-label">🛣️ Street</div>
              </div>
            </div>
          )}

          {/* Loading */}
          {loadingHouse && (
            <div className="dm-sheet-loading">
              <div className="dm-spinner" /> Looking up...
            </div>
          )}

          {/* Owner + Property Info */}
          {houseData && !loadingHouse && (
            <div className="dm-owner-section">
              {houseData.error ? (
                <div className="dm-owner-err">{houseData.error}</div>
              ) : (
                <>
                  {/* Owner name + occupancy */}
                  <div className="dm-owner-row">
                    <div className="dm-owner-name">{houseData.ownerName || 'Unknown Owner'}</div>
                    {houseData.ownerOccupied != null && (
                      <span className={`dm-occ-badge ${houseData.ownerOccupied ? 'occ' : 'abs'}`}>
                        {houseData.ownerOccupied ? '🏠 Homeowner Occupied' : '🚫 NOT Occupied by Owner'}
                      </span>
                    )}
                  </div>

                  {/* Quick stats */}
                  <div className="dm-stats-row">
                    {houseData.assessedValue && (
                      <div className="dm-stat-chip">
                        <span className="dm-stat-lbl">Value</span>
                        <span className="dm-stat-val">${houseData.assessedValue >= 1e6
                          ? (houseData.assessedValue / 1e6).toFixed(1) + 'M'
                          : Math.round(houseData.assessedValue / 1000) + 'K'}</span>
                      </div>
                    )}
                    {houseData.sqft && (
                      <div className="dm-stat-chip">
                        <span className="dm-stat-lbl">Sqft</span>
                        <span className="dm-stat-val">{houseData.sqft.toLocaleString()}</span>
                      </div>
                    )}
                    {houseData.yearBuilt && (
                      <div className="dm-stat-chip">
                        <span className="dm-stat-lbl">Built</span>
                        <span className="dm-stat-val">{houseData.yearBuilt}</span>
                      </div>
                    )}
                    {houseData.roofSqft && (
                      <div className="dm-stat-chip">
                        <span className="dm-stat-lbl">Roof</span>
                        <span className="dm-stat-val">{houseData.roofSqft.toLocaleString()}</span>
                      </div>
                    )}
                    {houseData.roofAge != null && (
                      <div className={`dm-stat-chip ${houseData.roofAge >= 20 ? 'danger' : ''}`}>
                        <span className="dm-stat-lbl">Roof Age</span>
                        <span className="dm-stat-val">{houseData.roofAge}yr</span>
                      </div>
                    )}
                    {houseData.roofType && (
                      <div className="dm-stat-chip">
                        <span className="dm-stat-lbl">Type</span>
                        <span className="dm-stat-val">{houseData.roofType}</span>
                      </div>
                    )}
                  </div>

                  {/* Cost estimate */}
                  {houseData.costEstimates?.asphalt && (
                    <div className="dm-cost-row">
                      Replacement: <strong>${Math.round(houseData.costEstimates.asphalt.low / 1000)}K–${Math.round(houseData.costEstimates.asphalt.high / 1000)}K</strong> asphalt
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Assign to team member */}
          <div className="dm-crew-row">
            <span className="dm-crew-label">Add by:</span>
            {TEAM_MEMBERS.map(m => (
              <button key={m.id}
                className={`dm-crew-btn ${assignedTo === m.id ? 'active' : ''}`}
                style={{ '--crew-color': m.color }}
                onClick={() => setAssignedTo(prev => prev === m.id ? null : m.id)}>
                <span className="dm-crew-avatar">{m.avatar}</span>
                {m.name}
              </button>
            ))}
          </div>

          {/* Tags grid */}
          <div className="dm-tags-section">
            <div className="dm-tags-grid">
              {DRIVE_TAGS.map(tag => (
                <button key={tag.value}
                  className={`dm-tag ${activeTags.includes(tag.value) ? 'on' : ''}`}
                  style={{ '--tc': tag.color }}
                  onClick={() => toggleTag(tag.value)}>
                  <span>{tag.icon}</span>
                  <span>{tag.label}</span>
                </button>
              ))}
              <button className="dm-tag custom-tag"
                onClick={() => setShowCustomTag(!showCustomTag)}>
                <span>+</span><span>Custom</span>
              </button>
            </div>
            {showCustomTag && (
              <div className="dm-custom-row">
                <input value={customTag} onChange={e => setCustomTag(e.target.value)}
                  placeholder="e.g. pool, flat roof..." className="dm-custom-input"
                  onKeyDown={e => e.key === 'Enter' && addCustomTag()} autoFocus />
                <button className="dm-custom-add" onClick={addCustomTag}>Add</button>
              </div>
            )}
            {activeTags.filter(t => !DRIVE_TAGS.find(d => d.value === t)).map(t => (
              <span key={t} className="dm-custom-chip">
                🏷️ {t.replace(/_/g, ' ')}
                <button onClick={() => toggleTag(t)}>✕</button>
              </span>
            ))}
          </div>

          {/* Photo + Notes */}
          <div className="dm-action-row">
            <button className="dm-photo-btn" onClick={() => photoInputRef.current?.click()}>
              📸 {photos.length > 0 ? `(${photos.length})` : 'Photo'}
            </button>
            <input type="text" className="dm-notes" value={notes}
              onChange={e => setNotes(e.target.value)} placeholder="Quick notes..." />
          </div>

          {/* Photo thumbs */}
          {photos.length > 0 && (
            <div className="dm-photos">
              {photos.map((p, i) => (
                <div key={i} className="dm-photo-thumb">
                  <img src={p.dataUrl} alt="" />
                  <button onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Save / Skip */}
          <div className="dm-save-row">
            <button className="dm-skip" onClick={dismissHouse}>Skip</button>
            <button className="dm-save" onClick={saveHouse}
              disabled={saving || (activeTags.length === 0 && photos.length === 0 && !notes)}>
              {saving ? 'Saving...' : `Save to CRM${activeTags.length ? ` (${activeTags.length})` : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* ===== SUMMARY PANEL ===== */}
      {showSummary && (
        <div className="dm-summary">
          <div className="dm-summary-head">
            <h3>Today's Session</h3>
            <button onClick={() => setShowSummary(false)}>✕</button>
          </div>

          <div className="dm-summary-nums">
            <div className="dm-sum-n"><div className="dm-sum-v">{stats.total}</div><div className="dm-sum-l">Houses</div></div>
            <div className="dm-sum-n"><div className="dm-sum-v">{stats.withPhotos}</div><div className="dm-sum-l">Photos</div></div>
            <div className="dm-sum-n"><div className="dm-sum-v">{stats.zips.length}</div><div className="dm-sum-l">ZIPs</div></div>
          </div>

          {/* By crew */}
          {Object.keys(stats.byCrew).length > 0 && (
            <div className="dm-summary-section">
              <h4>By Team Member</h4>
              {Object.entries(stats.byCrew).map(([id, count]) => {
                const member = TEAM_MEMBERS.find(m => m.id === id);
                return (
                  <div key={id} className="dm-sum-row">
                    <span className="dm-sum-crew-dot" style={{ background: member?.color || '#888' }}>{member?.avatar || id[0]}</span>
                    <span className="dm-sum-name">{member?.name || id}</span>
                    <span className="dm-sum-count">{count}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tag breakdown → Flyer matching */}
          <div className="dm-summary-section">
            <h4>Flyer Matching</h4>
            {Object.entries(stats.tagCounts).length === 0 ? (
              <div className="dm-sum-empty">No tags yet — start tagging houses!</div>
            ) : (
              Object.entries(stats.tagCounts).sort(([, a], [, b]) => b - a).map(([tag, count]) => {
                const td = DRIVE_TAGS.find(t => t.value === tag);
                return (
                  <div key={tag} className="dm-sum-row">
                    <span className="dm-sum-icon">{td?.icon || '🏷️'}</span>
                    <span className="dm-sum-name">{td?.label || tag.replace(/_/g, ' ')}</span>
                    <span className="dm-sum-count">{count}</span>
                    {td?.flyer && (
                      <span className="dm-sum-flyer">→ {td.flyer.replace('Letter for app ', '').replace('.docx', '')}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Recent logs */}
          <div className="dm-summary-section">
            <h4>Recent ({driveLog.length} total)</h4>
            <div className="dm-sum-log">
              {driveLog.slice(0, 20).map((entry, i) => (
                <div key={entry.id || i} className="dm-sum-log-item"
                  onClick={() => {
                    setShowSummary(false);
                    if (mapRef.current) {
                      mapRef.current.panTo({ lat: entry.lat, lng: entry.lng });
                      mapRef.current.setZoom(19);
                    }
                  }}>
                  <div className="dm-log-addr">{entry.streetAddress || entry.address}</div>
                  <div className="dm-log-meta">
                    {entry.ownerName && <span className="dm-log-owner">{entry.ownerName}</span>}
                    {entry.assignedTo && (() => {
                      const m = TEAM_MEMBERS.find(t => t.id === entry.assignedTo);
                      return <span className="dm-log-crew" style={{ background: m?.color }}>{m?.avatar}</span>;
                    })()}
                    {(entry.tags || []).slice(0, 3).map(t => {
                      const td = DRIVE_TAGS.find(d => d.value === t);
                      return <span key={t} className="dm-log-tag" style={{ background: td?.color || '#666' }}>
                        {td?.icon} {td?.label || t}
                      </span>;
                    })}
                    {entry.photos?.length > 0 && <span className="dm-log-photos">📷{entry.photos.length}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="dm-summary-actions">
            <button className="dm-sum-clear" onClick={() => {
              if (window.confirm('Clear all drive log entries?')) {
                setDriveLog([]);
                localStorage.removeItem('roofleads_drive_log');
              }
            }}>Clear Log</button>
            <button className="dm-sum-back" onClick={() => setShowSummary(false)}>Back to Map</button>
          </div>
        </div>
      )}

      {/* ===== DEMO COACHING — TOP TOAST (non-overlapping) ===== */}
      {demoActive && demoCoach && (
        <div className="dm-coach-toast">
          <div className="dm-coach-msg">{demoCoach}</div>
          <div className="dm-coach-controls">
            <span className="dm-coach-dots">
              {DEMO_ROUTE.map((_, i) => (
                <span key={i} className={`dm-coach-dot ${i === demoStep ? 'active' : i < demoStep ? 'done' : ''}`} />
              ))}
            </span>
            <button className="dm-coach-skip-btn" onClick={stopDemo}>Skip</button>
            <button className="dm-coach-next-btn" onClick={advanceDemo}>
              {demoStep >= DEMO_ROUTE.length - 1 ? 'Done ✓' : 'Next →'}
            </button>
          </div>
        </div>
      )}

      {/* ===== LEADS DRAWER ===== */}
      {showLeadsDrawer && (
        <>
          <div className="dm-leads-bg" onClick={() => setShowLeadsDrawer(false)} />
          <div className="dm-leads-drawer">
            <div className="dm-leads-head">
              <h3>My Leads</h3>
              <span className="dm-leads-count">{filteredLeads.length}</span>
              <button className="dm-leads-close" onClick={() => setShowLeadsDrawer(false)}>✕</button>
            </div>
            <input
              type="text" className="dm-leads-search" value={leadsSearch}
              onChange={e => setLeadsSearch(e.target.value)}
              placeholder="Search address, owner, ZIP..."
              autoFocus
            />
            <div className="dm-leads-list">
              {filteredLeads.length === 0 ? (
                <div className="dm-leads-empty">
                  {leadsSearch ? 'No matches found' : 'No leads yet. Draw a boundary or search an address in the main app.'}
                </div>
              ) : (
                filteredLeads.map((lead, i) => (
                  <div key={i} className="dm-leads-item" onClick={() => goToLead(lead)}>
                    <div className="dm-leads-dot" style={{
                      background: lead.leadQuality === 'hot' ? '#ef4444'
                        : lead.source === 'crm' ? '#22c55e' : '#3b82f6'
                    }} />
                    <div className="dm-leads-info">
                      <div className="dm-leads-addr">{lead.streetAddress}</div>
                      <div className="dm-leads-meta">
                        {lead.ownerName && <span>{lead.ownerName}</span>}
                        {lead.city && <span> · {lead.city} {lead.zip}</span>}
                        {lead.tag && <span className="dm-leads-tag">{lead.tag}</span>}
                      </div>
                    </div>
                    <div className="dm-leads-go">→</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DriveMode;
